import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ContactTableColumnId } from "./contacts-column-view.js";
import {
  DEFAULT_COLUMN_ORDER,
  DEFAULT_HIDDEN_COLUMNS,
  DEFAULT_PINNED_COLUMNS,
  appendPinnedColumn,
  contactTableVisibleColumns,
  moveContactTableColumn,
  pinnedVisibleColumns,
  removePinnedColumn,
} from "./contacts-column-view.js";
import {
  CONTACTS_TABLE_COLUMNS_STORAGE_KEY,
  defaultContactsTableColumnPrefs,
  parseContactsTableColumnPrefs,
  serializeContactsTableColumnPrefs,
} from "./contacts-column-prefs.js";

function loadStoredPrefs() {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    return parseContactsTableColumnPrefs(
      window.localStorage.getItem(CONTACTS_TABLE_COLUMNS_STORAGE_KEY),
    );
  } catch {
    return null;
  }
}

function saveStoredPrefs(prefs: {
  order: ContactTableColumnId[];
  hidden: ContactTableColumnId[];
  pinned: ContactTableColumnId[];
}): void {
  try {
    window.localStorage.setItem(
      CONTACTS_TABLE_COLUMNS_STORAGE_KEY,
      serializeContactsTableColumnPrefs(prefs),
    );
  } catch {
    // Quota / private mode — ignore
  }
}

export function useContactTableColumns(): {
  columnOrder: ContactTableColumnId[];
  visibleColumns: ContactTableColumnId[];
  hiddenColumns: Set<ContactTableColumnId>;
  pinnedColumns: Set<ContactTableColumnId>;
  pinnedVisible: ContactTableColumnId[];
  toggleColumn: (id: ContactTableColumnId) => void;
  pinColumn: (id: ContactTableColumnId) => void;
  unpinColumn: (id: ContactTableColumnId) => void;
  togglePin: (id: ContactTableColumnId) => void;
  moveColumn: (id: ContactTableColumnId, direction: "up" | "down") => void;
  reorderColumn: (fromId: ContactTableColumnId, toId: ContactTableColumnId) => void;
  resetColumns: () => void;
  showAllColumns: () => void;
} {
  const stored = loadStoredPrefs() ?? defaultContactsTableColumnPrefs();
  const [columnOrder, setColumnOrder] = useState<ContactTableColumnId[]>(() => stored.order);
  const [hiddenColumns, setHiddenColumns] = useState<Set<ContactTableColumnId>>(
    () => new Set(stored.hidden),
  );
  const [pinnedOrder, setPinnedOrder] = useState<ContactTableColumnId[]>(() => stored.pinned);
  const pinnedOrderRef = useRef(pinnedOrder);
  pinnedOrderRef.current = pinnedOrder;

  const pinnedColumns = useMemo(() => new Set(pinnedOrder), [pinnedOrder]);

  const visibleColumns = useMemo(
    () => contactTableVisibleColumns(columnOrder, hiddenColumns, pinnedOrder),
    [columnOrder, hiddenColumns, pinnedOrder],
  );

  const pinnedVisible = useMemo(
    () => pinnedVisibleColumns(pinnedOrder, hiddenColumns),
    [pinnedOrder, hiddenColumns],
  );

  useEffect(() => {
    saveStoredPrefs({
      order: columnOrder,
      hidden: [...hiddenColumns],
      pinned: pinnedOrder,
    });
  }, [columnOrder, hiddenColumns, pinnedOrder]);

  const toggleColumn = useCallback(
    (id: ContactTableColumnId) => {
      setHiddenColumns((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
          return next;
        }
        const wouldHideAll =
          columnOrder.filter((colId) => colId !== id && !next.has(colId)).length === 0;
        if (wouldHideAll) {
          return prev;
        }
        next.add(id);
        return next;
      });
    },
    [columnOrder],
  );

  const pinColumn = useCallback((id: ContactTableColumnId) => {
    setPinnedOrder((prev) => appendPinnedColumn(prev, id));
  }, []);

  const unpinColumn = useCallback((id: ContactTableColumnId) => {
    setPinnedOrder((prev) => removePinnedColumn(prev, id));
  }, []);

  const togglePin = useCallback((id: ContactTableColumnId) => {
    setPinnedOrder((prev) =>
      prev.includes(id) ? removePinnedColumn(prev, id) : appendPinnedColumn(prev, id),
    );
  }, []);

  const moveColumn = useCallback((id: ContactTableColumnId, direction: "up" | "down") => {
    setColumnOrder((prev) => {
      const index = prev.indexOf(id);
      if (index < 0) return prev;
      const target = direction === "up" ? index - 1 : index + 1;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      const [item] = next.splice(index, 1);
      next.splice(target, 0, item!);
      return next;
    });
  }, []);

  const reorderColumn = useCallback(
    (fromId: ContactTableColumnId, toId: ContactTableColumnId) => {
      if (fromId === toId) return;
      const pinned = pinnedOrderRef.current;
      const fromPinned = pinned.includes(fromId);
      const toPinned = pinned.includes(toId);
      if (fromPinned && toPinned) {
        setPinnedOrder(moveContactTableColumn(pinned, fromId, toId));
        return;
      }
      if (!fromPinned && !toPinned) {
        setColumnOrder((prev) => moveContactTableColumn(prev, fromId, toId));
      }
    },
    [],
  );

  const resetColumns = useCallback(() => {
    setColumnOrder(DEFAULT_COLUMN_ORDER);
    setHiddenColumns(new Set(DEFAULT_HIDDEN_COLUMNS));
    setPinnedOrder([...DEFAULT_PINNED_COLUMNS]);
  }, []);

  const showAllColumns = useCallback(() => {
    setHiddenColumns(new Set());
  }, []);

  return {
    columnOrder,
    visibleColumns,
    hiddenColumns,
    pinnedColumns,
    pinnedVisible,
    toggleColumn,
    pinColumn,
    unpinColumn,
    togglePin,
    moveColumn,
    reorderColumn,
    resetColumns,
    showAllColumns,
  };
}
